// Names for ids: centers, classrooms, courses and teachers, archived included (old lessons still show them).

import type { ApiResult } from "../api/client";
import { fetchCenters } from "../api/centers";
import { fetchClassrooms } from "../api/classrooms";
import { fetchCourses } from "../api/courses";
import { fetchTeachers } from "../api/teachers";
import type { Center, Classroom, Course, Slot, Teacher } from "../api/types";
import { formatWeekdayShort, t } from "../i18n/i18n";
import { formatRange } from "../time";

export interface ReferenceData {
  centers: Center[];
  classrooms: Classroom[];
  courses: Course[];
  teachers: Teacher[];
}

/** Loads all four lists; the first problem wins. */
export async function loadReferenceData(): Promise<ApiResult<ReferenceData>> {
  const centers: ApiResult<Center[]> = await fetchCenters(true);
  const classrooms: ApiResult<Classroom[]> = await fetchClassrooms(true);
  const courses: ApiResult<Course[]> = await fetchCourses(true);
  const teachers: ApiResult<Teacher[]> = await fetchTeachers(true);
  if (centers.ok === false) {
    return centers;
  }

  if (classrooms.ok === false) {
    return classrooms;
  }

  if (courses.ok === false) {
    return courses;
  }

  if (teachers.ok === false) {
    return teachers;
  }

  const data: ReferenceData = {
    centers: centers.data, classrooms: classrooms.data, courses: courses.data, teachers: teachers.data,
  };
  return { ok: true, data };
}

export function teacherName(data: ReferenceData, teacherId: string): string {
  const teacher: Teacher | undefined = data.teachers.find((candidate: Teacher): boolean => candidate.id === teacherId);
  return teacher === undefined ? t("app.none") : `${teacher.firstName} ${teacher.lastName}`;
}

export function courseName(data: ReferenceData, courseId: string): string {
  const course: Course | undefined = data.courses.find((candidate: Course): boolean => candidate.id === courseId);
  return course === undefined ? t("app.none") : course.name;
}

/** "N-101, North". */
export function classroomWithCenter(data: ReferenceData, classroomId: string): string {
  const classroom: Classroom | undefined = data.classrooms.find(
    (candidate: Classroom): boolean => candidate.id === classroomId);
  if (classroom === undefined) {
    return t("app.none");
  }

  const center: Center | undefined = data.centers.find((candidate: Center): boolean => candidate.id ===
    classroom.centerId);
  return center === undefined ? classroom.name : `${classroom.name}, ${center.name}`;
}

/** "Mon 16:00–18:00, Wed 16:00–18:00". */
export function describeSlots(slots: Slot[]): string {
  return slots.map((slot: Slot): string => `${formatWeekdayShort(slot.day)} ${formatRange(slot.range)}`).join(", ");
}
